<template>
    <div class="kanban-board">
        <div v-if="showScrollRightButton" class="scroll-right-task" @click="scrollRight">
            →
        </div>
        <div v-if="showScrollLeftButton" class="scroll-left-task" @click="scrollLeft">
            ←
        </div>
        <draggable
            class="supsis-draggable"
            :options="taskGroupDragOptions"
            v-model="groups"
            @start="() => (moving = true)"
            @end="movePipelineEnd"
        >
            <transition-group tag="div" class="drag-container">
                <TaskColumnCard
                    v-for="item of groups"
                    :key="item._id"
                    :pipeline="item"
                    :moving="moving"
                    :workflow-id="workflowId"
                    @moved="moveActionEnd"
                    @move-back="getListCard"
                    @pipeline-form="createPipelineForm"
                    @delete-pipeline="deletePipeline"
                    @create-pipeline="createPipeline"
                    @update-pipeline="updatePipeline"
                    @delete-modal-task="openDeleteTaskModal"
                    @confirm-modal-task="confirmModalOpen"
                />
            </transition-group>
        </draggable>
        <DeleteModal
            v-if="deleteTaskModal"
            :description="$t('task.deleteTaskCont')"
            :btnCancelText="$t('message.cancel')"
            :btnoktext="$t('message.delete')"
            @cancel="deleteTaskModal = false"
            @confirm="softDeleteTask"
        />
        <ConfirmModal
            v-if="confirmTaskModal"
            :description="'Görevi Tamamlandı Listesine Eklemek İstediğinize Emin Misiniz?'"
            :btnCancelText="$t('message.cancel')"
            :btnoktext="'Evet'"
            @cancel="confirmTaskModal = false"
            @confirm="confirmTask"
        />
    </div>
</template>
<script>
import Draggable from "vuedraggable";
import TaskColumnCard from "./TaskColumnCard.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import DeleteModal from "./DeleteModal.vue";
import { PERMISSION_TYPES } from "../../../../language/permissionTypes";
import ConfirmModal from "./ConfirmModal.vue";

export default {
    name: "TaskDraggable",
    components: {
        Draggable,
        TaskColumnCard,
        DeleteModal,
        ConfirmModal,
    },
    props: {
        workflowId: {
            type: String,
            default: null,
        },
        assignees: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        ...mapState({
            defaultWorkflow: (state) => state.tasks.defaultWorkflow,
            tasks: (state) => state.tasks.tasks,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
    },
    data: () => ({
        showScrollRightButton: false,
        showScrollLeftButton: false,
        taskGroupDragOptions: {
            group: "taskPipeline",
            animation: 200,
        },
        groups: [],
        listeners: [],
        paginations: {},
        moving: false,
        deleteTaskModal: false,
        confirmTaskModal: false,
        basicTaskForm: {},
        workflowPerm: {},
        confirmTaskData: {},
        deleteTaskData: {},
    }),
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
            getTasks: "tasks/getTasks",
            updateTaskPosition: "tasks/updateTaskPosition",
            updatePipelinePosition: "tasks/updatePipelinePosition",
            setWorkflows: "tasks/setWorkflows",
            setDefaultWorkflows: "tasks/setDefaultWorkflows",
            deleteTask: "tasks/deleteTask",
            getPaginationTasks: "tasks/getPaginationTasks",
            getWorkflowPipelines: "tasks/getWorkflowPipelines",
            clearTasks: "tasks/clearTasks",
            createPipelineStage: "tasks/createPipelineStage",
            updatePipelineStage: "tasks/updatePipelineStage",
            doneTask: "tasks/doneTask",
        }),
        handleMouseMove(event) {
            const board = document.querySelector(".kanban-board");
            const boardRect = board.getBoundingClientRect();
            const threshold = 50;
            const mouseXRelativeToBoard = event.clientX - boardRect.left;

            const nearRightEdge = mouseXRelativeToBoard > board.offsetWidth - threshold;
            const nearLeftEdge = mouseXRelativeToBoard < threshold;

            const canScrollLeft = board.scrollLeft > 0;
            const canScrollRight = board.scrollLeft + board.clientWidth < board.scrollWidth;

            this.showScrollRightButton = nearRightEdge && canScrollRight;
            this.showScrollLeftButton = nearLeftEdge && canScrollLeft;
        },
        scrollLeft() {
            const container = document.querySelector(".kanban-board");
            container.scrollBy({
                top: 0,
                left: -100,
                behavior: "smooth",
            });
        },
        scrollRight() {
            const container = document.querySelector(".kanban-board");
            container.scrollBy({
                top: 0,
                left: 100,
                behavior: "smooth",
            });
        },
        clearListener() {
            this.listeners.map((removeListener) => removeListener());
            this.listeners = [];
        },
        registerListener() {
            this.clearListener();
            const rows = Array.from(document.querySelectorAll(".drag-pipeline-rows"));
            (rows || []).map((row) => {
                const listener = (e) => {
                    const scrollHeight = row.offsetHeight + row.scrollTop;
                    if (row.scrollHeight === scrollHeight) {
                        const pipeLine = this.groups.find((g) => g._id === row.id);
                        if (pipeLine.tasks.length < pipeLine.taskCount) {
                            this.paginations = {
                                ...this.paginations,
                                [row.id]: {
                                    page: (this.paginations[row.id].page || 1) + 1,
                                    perPage: this.paginations[row.id].perPage,
                                },
                            };
                            this.getPaginationTasks({
                                workflowId: this.defaultWorkflow._id,
                                pipelineId: row.id,
                                page: this.paginations[row.id].page,
                                perPage: this.paginations[row.id].perPage,
                            });
                        }
                    }
                };
                this.listeners.push(() => row?.removeEventListener("scroll", listener));
                row?.addEventListener("scroll", listener);
            });
        },
        moveActionEnd(event) {
            if (this.checkPermission(PERMISSION_TYPES["task.update"].tr.name)) {
                const taskId = event.clone.id;
                const pipelineId = event.to.id;
                const fromPipelineId = event.from.id;
                const pipeLine = this.groups.find((g) => g._id === pipelineId);
                const firstTask = pipeLine.tasks[event.newIndex - 1];
                const secondTask = pipeLine.tasks[event.newIndex + 1];
                this.updateTaskPosition({
                    taskId,
                    payload: {
                        firstTaskId: firstTask?._id,
                        secondTaskId: secondTask?._id,
                        workflowId: this.defaultWorkflow._id,
                        pipelineStageId: pipeLine._id,
                        fromPipelineId,
                    },
                    groups: this.groups,
                });
            } else {
                this.$message.error(this.$t("permissionErrors.taskUpdate"));
                const fromGroup = this.groups.find((group) => group._id === event.from.id);
                const toGroup = this.groups.find((group) => group._id === event.to.id);
                const movedItem = toGroup.tasks[event.newIndex];
                toGroup.tasks.splice(event.newIndex, 1);
                fromGroup.tasks.splice(event.oldIndex, 0, movedItem);
            }
        },
        movePipelineEnd(event) {
            this.moving = false;
            const pipeline = this.groups[event.newIndex];
            if (pipeline.isNewColumn) {
                this.groups[event.newIndex].order = event.newIndex + 1;
                this.setWorkflows(
                    Object.keys(this.workflows).map((workflowId) => {
                        return workflowId === this.defaultWorkflow._id
                            ? { ...this.workflows[workflowId], pipeline: this.groups }
                            : this.workflows[workflowId];
                    })
                );
                this.setDefaultWorkflows({
                    ...this.defaultWorkflow,
                    pipeline: this.groups,
                });
            } else {
                this.updatePipelinePosition({
                    workflowId: this.defaultWorkflow._id,
                    pipelineId: this.groups[event.newIndex]._id,
                    position: event.newIndex + 1,
                }).then(() => {
                    this.setWorkflows(
                        Object.keys(this.workflows).map((workflowId) => {
                            return workflowId === this.defaultWorkflow._id
                                ? { ...this.workflows[workflowId], pipeline: this.groups }
                                : this.workflows[workflowId];
                        })
                    );
                    this.setDefaultWorkflows({
                        ...this.defaultWorkflow,
                        pipeline: this.groups,
                    });
                });
            }
        },
        groupsData() {
            const defaultWorkflow = (this.defaultWorkflow.pipeline || []).map((pipe) => {
                this.paginations[pipe._id] = {
                    page: this.paginations[pipe._id]?.page || 1,
                    perPage: this.paginations[pipe._id]?.perPage || 15,
                };
                return {
                    ...pipe,
                    tasks: this.tasks[pipe._id] || [],
                };
            });
            this.groups = defaultWorkflow;
            setTimeout(() => this.registerListener(), 100);
        },
        getFirstTasks() {
            this.getTasks({
                workflowId: this.defaultWorkflow._id,
                assignees: this.assignees,
            });
        },
        createPipelineForm(pipeline) {
            const newColumn = this.groups.findIndex((col) => col?.isNewColumn);
            if (newColumn >= 0) {
                return;
            }
            this.groups = this.groups.reduce((acc, col) => {
                const lastElement = [...acc].pop();
                acc.push({
                    ...col,
                    order: (lastElement?.order || 1) + 1,
                });
                if (col._id === pipeline._id) {
                    acc.push({
                        _id: Date.now(),
                        name: null,
                        type: col.type,
                        color: col.color,
                        order: (lastElement?.order || 1) + 1,
                        taskCount: 0,
                        tasks: [],
                        isNewColumn: true,
                    });
                }
                return acc;
            }, []);
            this.setDefaultWorkflows({
                ...this.defaultWorkflow,
                pipeline: this.groups,
            }).then(() => {
                const newColumn = this.groups.findIndex((col) => col?.isNewColumn);
                if (newColumn >= 0) {
                    this.$nextTick(() => {
                        const element = document.getElementById(this.groups[newColumn]._id);
                        element?.scrollIntoView({ behavior: "smooth" });
                    });
                }
            });
        },
        deletePipeline(pipeline) {
            this.groups = this.groups.filter((group) => group._id !== pipeline._id);
        },
        createPipeline(pipeline) {
            this.createPipelineStage({
                color: pipeline.color,
                name: pipeline.name,
                type: pipeline.type,
                order: pipeline.order,
            }).then(() => {
                this.$notification.success({
                    message: "Başarılı",
                    description: "Başarılı bir şekilde yeni alan eklendi.",
                });
            });
        },
        openDeleteTaskModal(task) {
            this.deleteTaskModal = true;
            this.deleteTaskData = task;
        },
        confirmModalOpen(task) {
            this.confirmTaskModal = true;
            this.confirmTaskData = task;
            console.log("taskkkk",this.confirmTaskData)
        },
        softDeleteTask() {
            this.deleteTask(this.deleteTaskData).then(() => {
                this.deleteTaskModal = false;
            });
        },
        confirmTask() {
            this.doneTask(this.confirmTaskData).then(() => {
                this.confirmTaskModal = false;
            });
        },
        updatePipeline(pipeline) {
            this.updatePipelineStage({
                pipelineId: pipeline._id,
                params: {
                    color: pipeline.color,
                    name: pipeline.name,
                    type: pipeline.type,
                },
            });
        },
        closeBasicTaskForm(pipeline) {
            this.basicTaskFormStatus = {
                ...this.basicTaskFormStatus,
                [pipeline._id]: this.basicTaskFormStatus[pipeline._id],
            };
        },
        createBasicTaskForm(pipeline) {
            if (this.basicTaskFormStatus[pipeline._id]) {
                this.basicTaskFormStatus = {
                    ...this.basicTaskFormStatus,
                    [pipeline._id]: false,
                };
            } else {
                Object.keys(this.basicTaskFormStatus).forEach((id) => {
                    this.basicTaskFormStatus[id] = false;
                });

                this.basicTaskFormStatus = {
                    ...this.basicTaskFormStatus,
                    [pipeline._id]: true,
                };

                this.basicTaskForm = {
                    ...this.basicTaskForm,
                    [pipeline._id]: {
                        title: null,
                        description: null,
                        assigne: null,
                    },
                };
            }
        },
        getListCard() {
            this.getWorkflowPipelines(this.workflowId).then((res) => {
                this.setDefaultWorkflows({
                    ...this.workflows[this.workflowId],
                    pipeline: res.data,
                });
                this.getFirstTasks();
            });
        },
        updateScrollButtons() {
            const container = document.querySelector(".kanban-board");
            this.showScrollLeftButton = container.scrollLeft > 0;
            this.showScrollRightButton = container.scrollLeft + container.clientWidth < container.scrollWidth;
        },
    },
    mounted() {
        window.addEventListener("mousemove", this.handleMouseMove);
        this.workflowPerm = this.workflows[this.workflowId];
        this.registerListener();
        this.getWorkflowPipelines(this.workflowId).then((res) => {
            this.setDefaultWorkflows({
                ...this.workflows[this.workflowId],
                pipeline: res.data,
            });
            this.getFirstTasks();
        });
        const container = document.querySelector(".kanban-board");
        container.addEventListener('scroll', this.updateScrollButtons);
    },
    beforeDestroy() {
        const container = document.querySelector(".kanban-board");
        if (container) {
            container.removeEventListener('scroll', this.updateScrollButtons);
        }
        window.removeEventListener("mousemove", this.handleMouseMove);
        this.clearListener();
    },
    watch: {
        defaultWorkflow: {
            deep: true,
            handler(newVal, oldVal) {
                this.groupsData();
                if (!this.$_.isEqual(oldVal, newVal)) {
                    this.workflowId = this.defaultWorkflow._id;
                }
            },
        },
        workflowId: {
            deep: true,
            handler() {
                this.getWorkflowPipelines(this.workflowId).then((res) => {
                    this.setDefaultWorkflows({
                        ...this.workflows[this.workflowId],
                        pipeline: res.data,
                    });
                    this.getFirstTasks();
                });
            },
        },
        assignees: {
            deep: true,
            handler() {
                this.getTasks({
                    workflowId: this.workflowId,
                    assignees: this.assignees,
                });
            },
        },
        tasks: {
            deep: true,
            handler() {
                this.groupsData();
            },
        },
    },
};
</script>
<style lang="scss">
$color: #ec4b1d;

.kanban-board {
    padding: 10px;
    overflow: auto;
    width: 100%;
    transition: 1s all;
    height: 100%;

    &::-webkit-scrollbar {
        display: none;
        height: 0;
    }

    .batch-operation {
        margin-bottom: 20px;
    }

    .supsis-draggable {
        height: 100%;

        &-card {
            height: calc(100% - 50px);
        }

        .pipeline-details {
            .pipeline-detail {
                font-weight: bold;
                font-size: 14px;
                display: flex;
                align-items: center;
                gap: 10px;
            }
        }
    }

    .drag-container {
        display: flex;
        gap: 10px;
        height: 100%;
    }

    .drag-pipeline-columns {
        height: 100%;

        .drag-pipeline-column-head {
            display: flex;
            gap: 10px;
            min-width: 300px;
            min-height: 50px;
            background-color: white;
            padding: 10px;
            border-radius: 4px;
            position: relative;

            .pipeline-head-content {
                display: flex;
                justify-content: space-between;
                width: 100%;
                align-items: center;
            }

            &:hover {
                .basic-task-container {
                    display: flex !important;
                }

                .column-options {
                    display: flex;
                }
            }

            .column-options {
                display: none;
                align-items: center;
                justify-content: center;
                gap: 5px;

                .material-icons {
                    font-size: 18px;
                    cursor: pointer;
                }
            }

            .basic-task-container {
                position: absolute;
                align-items: center;
                justify-content: center;
                width: 100%;
                bottom: -10px;
                left: 0px;
                display: none;

                .basic-task-add {
                    background-color: white;
                    box-shadow: 0 4px 10px rgba(34, 34, 34, 0.15);
                    border-radius: 4px;
                    padding: 4px 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }
            }

            .column-check {
                display: inline-flex;
                gap: 5px;
                justify-content: center;
                align-items: center;

                .kanban-checkbox {
                    display: flex;

                    .ant-checkbox.ant-checkbox-checked {
                        .ant-checkbox-inner {
                            border: 3px solid var(--pipeline-color) !important;
                            background-color: var(--pipeline-color) !important;
                        }
                    }

                    .ant-checkbox-checked::after {
                        border: 1px solid var(--pipeline-color) !important;
                    }

                    .ant-checkbox-wrapper:hover .ant-checkbox-inner,
                    .ant-checkbox:hover .ant-checkbox-inner,
                    .ant-checkbox-input:focus + .ant-checkbox-inner {
                        border: 1px solid var(--pipeline-color) !important;
                    }

                    .ant-checkbox-indeterminate .ant-checkbox-inner {
                        background-color: var(--pipeline-color);
                        border-color: var(--pipeline-color);

                        &::after {
                            height: 3px;
                            background-color: white;
                        }
                    }
                }
            }
        }

        .drag-pipeline-column-rows-length {
            font-size: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 15px;
            color: #5a5a5b;
        }
    }

    .drag-pipeline-rows {
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 4px;
        border-radius: 4px;
        overflow-y: auto;
        overflow-x: hidden;
        height: 100%;
        padding: 4px;
        background: linear-gradient(to bottom, #d3d6dc, white);

        .drag-pipeline-row {
            position: relative;
            width: 300px;
            border: 2px solid var(--white);
            border-radius: 4px;
            background: var(--white);

            .row-check {
                display: none;
                position: absolute;
                top: 0;
                left: 0;
                border-top-left-radius: 2px;
                overflow: hidden;
            }

            &:hover,
            &.selected {
                .row-check {
                    display: block;

                    .kanban-checkbox {
                        display: flex;

                        .ant-checkbox-inner {
                            border: none !important;
                            background: var(--pipeline-bg-color);
                        }

                        .ant-checkbox-checked::after {
                            border: none !important;
                        }

                        .ant-checkbox.ant-checkbox-checked {
                            .ant-checkbox-inner {
                                border: none !important;
                                background-color: var(--pipeline-color) !important;
                            }
                        }
                    }
                }
            }

            &.select {
                border: 2px solid var(--pipeline-color);
            }

            .kanban-task-details {
                display: flex;
                align-items: center;
                justify-content: space-between;
                cursor: pointer;

                .kanban-task-alt-items {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    margin-top: 10px;

                    &-item {
                        display: flex;
                        align-items: center;
                        gap: 3px;

                        .material-icons {
                            font-size: 14px;
                        }
                    }
                }
            }

            .drag-pipeline-row-card {
                border-radius: 5px;
                width: 100%;
                overflow: hidden;
                display: flex;
                min-height: 70px;

                .content {
                    cursor: pointer;
                    padding: 10px;
                    width: 100%;

                    .task-head {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        width: 100%;
                        cursor: pointer;

                        .task-title {
                            width: 90%;
                            font-weight: bold;
                            font-size: 14px;
                            color: #5a5a5b;
                        }
                    }

                    .task-more {
                        font-size: 16px;
                    }

                    .task-users {
                        display: flex;
                        align-items: center;
                        margin-top: 5px;
                        gap: 2px;
                    }
                }
            }
        }
    }

    .task-more-options {
        padding-top: 0 !important;

        .ant-popover-arrow {
            display: none !important;
        }

        .ant-popover-inner-content {
            box-shadow: none !important;
        }

        .ant-popover-inner-content {
            padding: 5px 0px;
        }

        .options-item {
            width: 200px;
            display: flex;
            align-items: center;
            padding: 5px 10px;
            gap: 10px;
            font-weight: 500;
            font-size: 15px;
            color: #55555a;
            cursor: pointer;

            &:hover {
                background-color: #f1f3f9;
            }

            .material-icons {
                color: #91a2b6;
            }
        }

        .bracket {
            width: 100%;
            height: 1px;
            margin: 3px 0px;
            background-color: #e6e9f2;
        }
    }
}


.scroll-right-task,
.scroll-left-task {
    position: fixed;
    top: 50%;
    background-color: #ec4b1d;
    color: white;
    border: none;
    padding: 10px;
    cursor: pointer;
    z-index: 1000;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 26px;
    width: 60px;
}

.scroll-right-task {
    right: 10px;
}

.scroll-left-task {
    left: 150px !important;
    z-index: 1;
}
</style>
